/* eslint-disable max-len */
import React, { FunctionComponent, useEffect, useState } from 'react';

import { Button, Heading, useBreakpoint } from '@savebetter/components';
import { Breakpoint } from '@savebetter/components/src/types';

import { useBankProducts } from '../context/BankProductsContext';
import { useDatasources } from '../context/DataSourceContext';
import {
  getProductIdFromVariable,
  replaceAverageVars,
  replaceComparedApyWithAvgVars,
  replaceCustomVars
} from '../utils/replaceCustomVar';

import FeaturedProductCard from './FeaturedProductCard';
import NationalAverageCard from './NationalAverageCard';
import { PageSectionBlok, PageSectionProps } from './PageSection';
import { PageSectionHeader } from './PageSectionHeader';
import { Section } from './Section';

export type LayoutType = 'row' | 'col';

const layoutClassMap: Record<LayoutType, string> = {
  row: 'flex-row',
  col: 'flex-col'
};

interface Blok extends PageSectionBlok {
  productVar: string;
  nationalAverageVar: string;
  layoutType: LayoutType;
  nationalAverageDisclaimer?: string;
  nationalAverageDescription?: string;
  comparisonTitle: string;
  comparisonDescription: string;
  buttonLink?: string;
  bankHexColor?: string;
  apyTitleColor: string;
  title: string;
}

interface ProductComparisonProps extends PageSectionProps {
  blok: Blok;
}

interface NationalAverageDisclaimerProps {
  nationalAverageDisclaimer: string;
  viewportType: Breakpoint;
}

export const getLayoutTypeClasses = (viewportType: Breakpoint, layoutType: LayoutType = 'row'): string => {
  if (viewportType === 'sm') {
    return 'w-full';
  }
  return layoutType === 'row' ? 'w-5/12' : 'w-1/3';
};

const NationalAverageDisclaimer: FunctionComponent<NationalAverageDisclaimerProps> = ({
  nationalAverageDisclaimer
}) => {
  const { contextProducts: products } = useBankProducts();
  const { contextDatasources: datasources } = useDatasources();

  return (
    <p className="w-full pt-8 text-sm text-center">
      <sup>1</sup> {replaceCustomVars(nationalAverageDisclaimer, products, datasources)}
    </p>
  );
};

export const ProductComparison: FunctionComponent<ProductComparisonProps> = ({ blok }) => {
  const {
    title,
    productVar,
    nationalAverageVar,
    nationalAverageDescription,
    nationalAverageDisclaimer,
    comparisonTitle,
    comparisonDescription,
    sectionClassName,
    bankHexColor,
    apyTitleColor,
    layoutType,
    buttonLink,
    hasBackground,
    hasBorder,
    hasPaddingTop,
    hasPaddingBottom
  } = blok;
  const breakpoint = useBreakpoint();
  const [layout, setLayout] = useState<LayoutType>('row');
  const { contextProducts: products } = useBankProducts();
  const { contextDatasources: datasources } = useDatasources();

  const productId = getProductIdFromVariable(productVar);
  const product = products?.find(({ id }) => id === productId);
  const nationalAverage = replaceAverageVars(nationalAverageVar, datasources);
  const nationalAverageType = nationalAverageVar.slice(1, 5);

  useEffect(() => {
    if (breakpoint === 'sm') {
      setLayout('col');
    } else {
      setLayout(layoutType);
    }
  }, [breakpoint, layoutType]);
  if (!products || !datasources || !product || !nationalAverage) {
    return null;
  }

  const comparingVar = `{${productId}-APY}${nationalAverageVar}`;
  const productComparisonValue = replaceComparedApyWithAvgVars(comparingVar, products, datasources);

  const layoutTypeClasses = layout === 'row' ? 'w-1/2' : 'w-full';

  return (
    <Section
      width="md"
      hasBackground={hasBackground}
      hasBorder={hasBorder}
      className={`${sectionClassName ? sectionClassName : ''}`}
      blok={blok}
      hasPaddingBottom={hasPaddingBottom}
      hasPaddingTop={hasPaddingTop}
    >
      <PageSectionHeader>{replaceCustomVars(title, products, datasources)}</PageSectionHeader>
      {product && nationalAverageVar && (
        <div className={`flex ${layoutClassMap[layout]} flex-wrap box-border`}>
          <div className={`${layoutTypeClasses} box-border justify-center flex flex-wrap`}>
            <FeaturedProductCard
              product={product}
              bankHexColor={bankHexColor}
              apyTitleColor={apyTitleColor}
              layoutType={layout}
              productComparisonValue={productComparisonValue}
              viewportType={breakpoint}
            />
            <NationalAverageCard
              nationalAverage={nationalAverage}
              nationalAverageType={nationalAverageType}
              layoutType={layout}
              nationalAverageDescription={nationalAverageDescription}
              viewportType={breakpoint}
            />
            {nationalAverageDisclaimer && layout === 'row' && (
              <NationalAverageDisclaimer
                nationalAverageDisclaimer={nationalAverageDisclaimer}
                viewportType={breakpoint}
              />
            )}
          </div>
          <div className={`${layoutTypeClasses} box-border pt-12 flex flex-col`}>
            <Heading className="text-center lg:pb-8" kind="3" level="3">
              {replaceCustomVars(comparisonTitle, products, datasources)}
            </Heading>
            <p className="text-center">{replaceCustomVars(comparisonDescription, products, datasources)}</p>
            {buttonLink && (
              <div className="mx-auto">
                <Button kind="primary" className="mt-12 lg:mt-16 w-max" href={buttonLink}>
                  Start Saving
                </Button>
              </div>
            )}
          </div>
          {nationalAverageDisclaimer && (breakpoint === 'sm' || layout === 'col') && (
            <NationalAverageDisclaimer
              nationalAverageDisclaimer={nationalAverageDisclaimer}
              viewportType={breakpoint}
            />
          )}
        </div>
      )}
    </Section>
  );
};
